'use-client';
import type { KeyboardEvent } from 'react';
import React from 'react';
import classNames from 'clsx';
import { keyCodes, getDataAttributes } from '@wix/editor-elements-common-utils';
import type { ISiteMembersDialogLayoutProps } from '../SiteMembersDialogLayout.types';
import Close from './assets/close.svg';
import style from './style/style.scss';

const SiteMembersDialogLayout: React.FC<
  ISiteMembersDialogLayoutProps
> = props => {
  const {
    id,
    className,
    isCloseable,
    children,
    translations,
    onCloseDialogCallback,
    headlineId,
    displayMode = 'fullscreen',
    dialogPosition = 'center',
    isCodeVerificationDialog,
    role,
  } = props;

  const dialogRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = React.useRef<HTMLElement | SVGElement>();

  // Focus management - following PopupRoot pattern
  React.useEffect(() => {
    const focusedElement = document.activeElement;
    if (focusedElement) {
      // Save pointer to element that was focused before opening dialog
      previouslyFocusedElement.current = focusedElement as
        | HTMLElement
        | SVGElement;
    }

    // Focus the dialog
    dialogRef.current?.focus();

    // After dialog is closed, restore focus to previously active element while preserving scroll position
    return () => {
      const prevFocusedElement = previouslyFocusedElement.current;
      // Previously focused element is still in page
      if (prevFocusedElement && document.body.contains(prevFocusedElement)) {
        prevFocusedElement.focus({ preventScroll: true });
      }
    };
  }, []);

  // Handle keyboard events
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.keyCode === keyCodes.escape) {
      onCloseDialogCallback();
    }
  };

  const closeButton = isCloseable ? (
    <button
      className={style.xButton}
      onClick={onCloseDialogCallback}
      data-testid="xButton"
      aria-label={translations.membersCloseDialog}
      type="button"
      tabIndex={0}
    >
      <Close role="img" />
    </button>
  ) : null;

  return (
    <>
      <div
        className={style.blockingLayer}
        onClick={onCloseDialogCallback}
        data-testid="siteMembersDialogBlockingLayer"
        data-layout={displayMode}
      />
      <div
        id={id}
        {...getDataAttributes(props)}
        ref={dialogRef}
        role={role ?? 'dialog'}
        aria-modal="true"
        tabIndex={-1}
        aria-labelledby={headlineId}
        className={classNames(className, style.dialog, {
          [style.withCloseButtonPadding]: isCloseable,
        })}
        data-testid="siteMembersDialogLayout"
        data-layout={displayMode}
        onKeyDown={handleKeyDown}
      >
        <div
          className={classNames(style.content, {
            [style.codeVerificationDialog]: isCodeVerificationDialog,
          })}
          data-dialogposition={dialogPosition}
        >
          {children}
        </div>
        {closeButton}
      </div>
    </>
  );
};

export default SiteMembersDialogLayout;
